// Level-of-detail pyramid over a clip.
//
// Each level halves the rows and/or columns of the one before with a
// max-reduce that ignores MISSING, so a one-sweep burst survives every zoom
// level. Levels are keyed by (kx, ky) so a 4908-bin, 360-row run can reduce
// its columns eight times without touching its rows.

import { MISSING } from '../model.ts'
import { clipCell, type Clip } from './layout.ts'

export interface Level {
  kx: number
  ky: number
  rows: number
  cols: number
  /** Row-major centi-dB; null means read through `at` from the clip. */
  data: Int16Array | null
  at: (row: number, col: number) => number
}

export class Pyramid {
  readonly clip: Clip
  readonly #levels = new Map<string, Level>()

  constructor(clip: Clip) {
    this.clip = clip
    const cols = clip.bin1 - clip.bin0
    const bin0 = clip.bin0
    this.#levels.set('0,0', {
      kx: 0,
      ky: 0,
      rows: clip.rows,
      cols,
      data: null,
      at: (row, col) => clipCell(clip, row, bin0 + col),
    })
  }

  get base(): Level {
    return this.#levels.get('0,0')!
  }

  /** The coarsest level that still has at least one cell per `pxPerCol` × `pxPerRow`. */
  choose(colsPerPx: number, rowsPerPx: number): Level {
    const kx = Math.max(0, Math.floor(Math.log2(Math.max(1, colsPerPx))))
    const ky = Math.max(0, Math.floor(Math.log2(Math.max(1, rowsPerPx))))
    return this.level(kx, ky)
  }

  level(kx: number, ky: number): Level {
    const key = `${kx},${ky}`
    const cached = this.#levels.get(key)
    if (cached) return cached

    // Reduce whichever axis is further from the base first, so the chain of
    // intermediate levels is short and shared with neighboring requests.
    const parent = kx >= ky && kx > 0 ? this.level(kx - 1, ky) : this.level(kx, ky - 1)
    const halveCols = parent.kx < kx
    const rows = halveCols ? parent.rows : Math.ceil(parent.rows / 2)
    const cols = halveCols ? Math.ceil(parent.cols / 2) : parent.cols
    if (rows < 1 || cols < 1) return parent

    const data = new Int16Array(rows * cols)
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        let best = MISSING
        if (halveCols) {
          const a = parent.at(r, c * 2)
          const b = c * 2 + 1 < parent.cols ? parent.at(r, c * 2 + 1) : MISSING
          best = a === MISSING ? b : b === MISSING ? a : a > b ? a : b
        } else {
          const a = parent.at(r * 2, c)
          const b = r * 2 + 1 < parent.rows ? parent.at(r * 2 + 1, c) : MISSING
          best = a === MISSING ? b : b === MISSING ? a : a > b ? a : b
        }
        data[r * cols + c] = best
      }
    }
    const level: Level = { kx, ky, rows, cols, data, at: (row, col) => data[row * cols + col]! }
    this.#levels.set(key, level)
    return level
  }
}
